import { computed } from '@vue/composition-api'
import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import access from './modules/access'
import buildings from './modules/buildings'
import categories from './modules/categories'
import ccd from './modules/ccd'
import currencies from './modules/currencies'
import dashboard from './modules/dashboard'
import departments from './modules/departments'
import directories from './modules/directories'
import ecircular from './modules/ecircular'
import form from './modules/form'
import hotb from './modules/hotb'
import listNavigation from './modules/listNavigation'
import language from './modules/language'
import postFitoutSubmission from './modules/postFitoutSubmission'
import projectContractors from './modules/projectContractors'
import projects from './modules/projects'
import regions from './modules/regions'
import settings from './modules/settings'
import tables from './modules/tables'
import tenancyContractors from './modules/tenancyContractors'
import tenancyRecords from './modules/tenancyRecords'
import tenantProject from './modules/tenantProject'
import tenders from './modules/tenders'
import user from './modules/user'
import workPermit from './modules/workPermit'

/// <reference types="@vue/composition-api" />

Vue.use(Vuex)

const debug = process.env.NODE_ENV !== 'production'

const modules = {
  access,
  ccd,
  directories,
  dashboard,
  ecircular,
  form,
  hotb,
  listNavigation,
  projectContractors,
  projects,
  settings,
  tenancyRecords,
  tenantProject,
  tenders,
  user,
  workPermit,
  tables,
  tenancyContractors,
  regions,
  language,
  buildings,
  categories,
  departments,
  postFitoutSubmission,
  currencies,
}

/**
 * @type {VuexStore<typeof modules>}
 */
const store = new Vuex.Store({
  modules,
  strict: debug,
  plugins: [
    createPersistedState({
      paths: ['user', 'access', 'settings', 'regions', 'language', 'buildings', 'currencies'],
    }),
  ],
})

export const useStore = () => store

/**
 * @template {keyof modules} T
 * @template {keyof modules[T]['getters']} K
 * @template {keys extends string ? [keys] : keys} KS
 * @param {T} moduleName
 * @param {Array<K> | K} keys
 * @return {{ [P in KS]: Ref<ReturnType<modules[T]['getters'][P]>> }}
 */
export const useStoreGetters = (moduleName, keys = []) => {
  const store = useStore()
  let curKeys = []
  if (Array.isArray(keys) && keys.length === 0) {
    curKeys = Object.keys(store.getters)
      .filter((key) => new RegExp(`^${moduleName}/`).test(key))
  } else {
    curKeys = (Array.isArray(keys) ? keys : [keys])
      .map((k) => `${moduleName}/${k}`)
  }
  return curKeys.reduce((obj, key) => {
    const curKey = key.replace(`${moduleName}/`, '')
    // Compatible with the existing key
    obj[curKey] = computed(() => store.getters[key])
    // Identify the current value as a Vue Ref
    obj[`${curKey}Ref`] = computed(() => store.getters[key])
    return obj
  }, {})
}

export const useStoreState = () => {
  const store = useStore()
  return computed(() => store.state)
}

/**
 * @typedef {T extends (ctx: any, ...args: infer R) => any ? R : T} ActionParameters
 * @template {(ctx: any, ...args: any[]) => any} T
 */
/**
 * @typedef {(...args: ActionParameters<T>) => ReturnType<T>} ActionFunction
 * @template {(ctx: any, ...args: any[]) => any} T
 */
/**
 * @template {keyof modules} T
 * @template {keyof modules[T]['actions']} KS
 * @param {T} namespace
 * @param {KS[]} actions
 * @returns {{ [P in KS]: ActionFunction<modules[T]['actions'][P]> }}
 */
export const useStoreActions = (namespace, actions = []) => {
  const module = store._modulesNamespaceMap[parseNamespace(namespace)]
  if (!module) return
  const result = actions.reduce((rs, keyName) => {
    rs[keyName] = function mappedAction (...args) {
      return module.context.dispatch.apply(store, [keyName].concat(args))
    }
    return rs
  }, {})
  return result
}

/**
 * @template {keyof modules} T
 * @template {{[name: string]: (...args: any[]) => any}} M
 * @param {T} namespace - Namespace of the Vuex module.
 * @param {M} mutations - An object that maps the mutation names to the actual mutation functions.
 * @returns {{ [K in keyof M]: (...args: Parameters<M[K]>) => void }} A reducer object that contains all the mapped mutations functions.
 */
export const useStoreMutations = (namespace, mutations = {}) => {
  const module = store._modulesNamespaceMap[parseNamespace(namespace)]
  if (!module) return
  const result = Object.keys(mutations).map(key => ({ key, val: mutations[key] })).reduce((rs, { key, val }) => {
    rs[key] = function mappedMutation (...args) {
      return module.context.commit.apply(store, [val].concat(args))
    }
    return rs
  }, {})
  return result
}

/**
 * @param {string} namespace
 * @returns {string}
 */
function parseNamespace (namespace) {
  if (namespace.charAt(namespace.length - 1) !== '/') {
    namespace += '/'
  }
  return namespace
}

export default store
